// UnitTabLogic.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Box, Tooltip, IconButton } from "@mui/material";
import Icon from "components/icon";
import {
  getUnits,
  saveUnit,
  deleteUnit,
} from "screens/Inventory/logic/UnitofMeasurementlogic";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useTheme } from "@mui/material/styles";

export const useUnitLogic = (toast) => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const theme = useTheme();

  // States
  const [loading, setLoading] = useState(false);
  const [openUnitDlg, setOpenUnitDlg] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rows, setRows] = useState([]);
  const [unit, setUnit] = useState({
    unitID: 0,
    unitName: "",
    unitSymbol: "",
    isActive: true,
  });
  const [existingUnit, setExistingUnit] = useState({ ...unit });
  const [unitContent, setUnitContent] = useState({
    entity: "create",
    title: "Create New Unit of Measure",
    btnText: "Save Unit",
  });
  const [errors, setErrors] = useState({
    unitName: false,
  });
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [searchText, setSearchText] = useState("");

  const handleSearch = (value) => {
    setSearchText(value);
    // Optional: filter rows here
  };

  useEffect(() => {
    const isEqual =
      unit && existingUnit && unit.unitName === existingUnit.unitName;
    setBtnDisabled(isEqual);
  }, [unit, existingUnit]);

  // Fetch units on component mount
  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      setLoading(true);

      const response = await getUnits(undefined, navigate);
      let unitsData = [];

      if (response && Array.isArray(response.result)) {
        unitsData = response.result;
      } else if (Array.isArray(response)) {
        unitsData = response;
      } else {
        unitsData = [];
      }

      if (unitsData.length > 0) {
        const formattedData = unitsData.map((unit) => ({
          id: unit.unitID,
          unitID: unit.unitID,
          unitName: unit.unitName,
          unitSymbol: unit.unitSymbol || "",
          isActive: unit.isActive,
        }));

        setRows(formattedData);
      } else {
        console.log("No units found");
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
      toast.error("Failed to fetch units");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUnitDlg = () => {
    setUnitContent({
      entity: "create",
      title: "Create New Unit of Measure",
      btnText: "Save Unit",
    });
    setUnit({
      unitID: 0,
      unitName: "",
      unitSymbol: "",
      isActive: true,
    });
    setExistingUnit({
      unitID: 0,
      unitName: "",
      unitSymbol: "",
      isActive: true,
    });
    setOpenUnitDlg(true);
  };

  const handleCloseUnitDlg = () => {
    setOpenUnitDlg(false);
    resetUnit();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setUnit((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const newErrors = {
      unitName: !unit.unitName,
    };

    setErrors(newErrors);

    if (
      Object.values(newErrors).some((error) => error !== false && error !== "")
    ) {
      toast.error("Please enter unit name");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        unitID: unit.unitID || 0,
        unitName: unit.unitName,
        unitSymbol: unit.unitSymbol,
        isActive: unit.isActive !== undefined ? unit.isActive : true,
      };

      const result = await saveUnit(payload, navigate);

      if (result && !result.isError) {
        toast.success(
          `Unit ${
            unitContent.entity === "create" ? "created" : "updated"
          } successfully`
        );
        handleCloseUnitDlg();
        fetchUnits();
        resetUnit();
      } else {
        toast.error(
          result?.message ||
            `Failed to ${
              unitContent.entity === "create" ? "create" : "update"
            } unit`
        );
      }
    } catch (error) {
      console.error("Error saving unit:", error);
      toast.error("An error occurred while saving unit");
    } finally {
      setLoading(false);
    }
  };

  const resetUnit = () => {
    setUnit({
      unitID: 0,
      unitName: "",
      unitSymbol: "",
      isActive: true,
    });
    setErrors({ unitName: false });
  };

  const showDeleteConfirmation = (id) => {
    MySwal.fire({
      title: "<strong>Are you sure?</strong>",
      html: "<span>You won't be able to revert this!</span>",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
      width: "390px",
      padding: "1rem",
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: "&times;",
      customClass: {
        popup:
          theme.palette.mode === "dark"
            ? "custom-swal-popup-dark"
            : "custom-swal-popup",
        title:
          theme.palette.mode === "dark"
            ? "custom-swal-title-dark"
            : "custom-swal-title",
        htmlContainer: "custom-swal-html",
        confirmButton: "custom-swal-confirm",
        cancelButton:
          theme.palette.mode === "dark"
            ? "custom-swal-cancel-dark"
            : "custom-swal-cancel",
        closeButton: "custom-swal-close",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteUnit(id);
      }
    });
  };

  const handleDeleteUnit = async (id) => {
    try {
      const result = await deleteUnit(id, navigate);

      if (result && result.isError) {
        toast.error(result.message, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      } else {
        fetchUnits();
        toast.success("Unit deleted successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error("Error deleting Unit:", error);
      toast.error("An error occurred while deleting unit");
    }
  };

  const handleUnitEdit = (id) => {
    const unitToEdit = rows.find((unit) => {
      console.log(`Checking: ${unit.unitID} === ${id}`, unit.unitID === id);
      return unit.unitID === id;
    });

    if (unitToEdit) {
      setUnitContent({
        entity: "edit",
        title: "Edit Unit of Measure",
        btnText: "Save Changes",
      });
      setUnit({
        unitID: unitToEdit.unitID,
        unitName: unitToEdit.unitName,
        unitSymbol: unitToEdit.unitSymbol,
        isActive:
          unitToEdit.isActive !== undefined ? unitToEdit.isActive : true,
      });
      setExistingUnit({
        unitID: unitToEdit.unitID,
        unitName: unitToEdit.unitName,
        unitSymbol: unitToEdit.unitSymbol,
        isActive:
          unitToEdit.isActive !== undefined ? unitToEdit.isActive : true,
      });

      setOpenUnitDlg(true);
    } else {
      toast.error("Unit not found");
    }
  };
  const filteredRows = rows.filter(
    (item) =>
      item.unitName?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.unitSymbol?.toLowerCase().includes(searchText.toLowerCase())
  );

  //#region Columns Configuration
  const columns = [
    {
      flex: 0.1,
      minWidth: 150,
      field: "unitName",
      headerName: "Unit Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.unitName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 100,
      field: "unitSymbol",
      headerName: "Symbol",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.unitSymbol || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 120,
      maxWidth: 120,
      headerName: "Action",
      renderCell: (params) => {
        const { row } = params;
        console.log("🔧 Action buttons for row:", row);
        return (
          <>
            <Tooltip title="Edit">
              <IconButton onClick={() => handleUnitEdit(row.unitID)}>
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                onClick={() => showDeleteConfirmation(row.unitID)}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  //#endregion Columns Configuration

  const UnitProps = {
    unitContent,
    resetUnit,
    unit,
    setUnit,
    existingUnit,
    handleChange,
    errors,
    handleSave,
    rows: filteredRows,
    fetchUnits,
    columns,
    paginationModel,
    setPaginationModel,
    openUnitDlg,
    handleOpenUnitDlg,
    handleCloseUnitDlg,
    btnDisabled,
    handleSearch,
    searchText,
    loading,
  };

  return {
    UnitProps,
  };
};
